import { useTheme } from "@mui/material";
import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { Trans } from "react-i18next";
import { ConfirmationDialog } from "../../../components";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import {
  LICENSE_ALLOCATIONS_QUERY_KEY,
  useExternalExperiences,
} from "../../../hooks/useExternalExperiences";
import { useLocalization } from "../../../hooks/useLocalization";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import {
  USER_RELEASE_CHANNELS_QUERY_KEY,
  UserExperienceRow,
} from "../hooks/useUserExperiences";

interface RemoveUserExperienceAccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  experience: UserExperienceRow;
  userId: string;
}

export const RemoveUserExperienceAccessDialog: FC<
  RemoveUserExperienceAccessDialogProps
> = ({ isOpen, onClose, experience, userId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();
  const { t } = useLocalization();

  const { releaseChannelIdToLicenseMap } = useExternalExperiences();

  const isExternalExperience = useMemo(() => {
    return releaseChannelIdToLicenseMap.has(experience.id);
  }, [experience.id, releaseChannelIdToLicenseMap]);

  const removeReleaseChannelMutation = useMutation({
    mutationFn: (releaseChannelId: string) =>
      userManagementService.removeReleaseChannelFromUser(
        userId,
        releaseChannelId,
      ),
    onSuccess: (_, releaseChannelId) => {
      queryClient.setQueryData(
        [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
        (old: UserManagement_V1.ReleaseChannel[] | undefined) => {
          return (
            old?.filter((rc) => rc.releaseChannelId !== releaseChannelId) ?? []
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
      });
      onClose();
    },
  });

  const deallocateLicenseUserSeatMutation = useMutation({
    mutationFn: ({ licenseId }: { licenseId: string }) =>
      licensingService.deallocateLicenseUserSeat(licenseId, userId),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [LICENSE_ALLOCATIONS_QUERY_KEY, variables.licenseId],
        (old: Licensing_V1.LicenseUserAllocation[] | undefined) => {
          return (
            old?.filter((allocation) => allocation.userId !== userId) ?? []
          );
        },
      );
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, variables.licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
      onClose();
    },
  });

  const handleRemoveConfirm = () => {
    if (isExternalExperience) {
      const license = releaseChannelIdToLicenseMap.get(experience.id);
      if (license) {
        deallocateLicenseUserSeatMutation.mutate({
          licenseId: license.licenseId,
        });
      }
    } else {
      removeReleaseChannelMutation.mutate(experience.id);
    }
  };

  const error = useMemo(() => {
    if (isExternalExperience) {
      return deallocateLicenseUserSeatMutation.error;
    } else {
      return removeReleaseChannelMutation.error;
    }
  }, [
    isExternalExperience,
    deallocateLicenseUserSeatMutation.error,
    removeReleaseChannelMutation.error,
  ]);

  const handleCancel = () => {
    removeReleaseChannelMutation.reset();
    deallocateLicenseUserSeatMutation.reset();
    onClose();
  };

  const isLoading = useMemo(() => {
    if (isExternalExperience) {
      return deallocateLicenseUserSeatMutation.isPending;
    } else {
      return removeReleaseChannelMutation.isPending;
    }
  }, [
    isExternalExperience,
    deallocateLicenseUserSeatMutation.isPending,
    removeReleaseChannelMutation.isPending,
  ]);

  return (
    <ConfirmationDialog.Root maxWidth="xs" onClose={handleCancel} open={isOpen}>
      <ConfirmationDialog.Title iconColor={theme.colors.orange} showIcon={true}>
        {t("users.removeExperienceAccessDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={error}>
        <ConfirmationDialog.Text>
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="users.removeExperienceAccessDialog.confirmation"
            values={{
              experienceTitle: experience.title,
            }}
          />
        </ConfirmationDialog.Text>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button onClick={handleCancel} variant="contained">
          {t("users.removeExperienceAccessDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          isLoading={isLoading}
          loadingText={t("users.removeExperienceAccessDialog.removing")}
          onClick={handleRemoveConfirm}
          variant="secondary"
        >
          {t("users.removeExperienceAccessDialog.remove")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
